import { Head, Link } from "@inertiajs/react"

import { HpiDataTable, HpiStatsBar } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import { teamPaymentsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface MileageRow {
  id: number
  date: string
  case_id: number
  case_name: string
  miles: number
  mileage_rate: number
  amount: number
}

interface ExpenseRow {
  id: number
  date: string
  case_id: number
  case_name: string
  amount: number
}

interface Props {
  employee: { id: number; name: string }
  payment_date: string
  payment_ref: string
  mileage: MileageRow[]
  cash_expenses: ExpenseRow[]
  mileage_total: number
  expense_total: number
  payment_total: number
  return_params: {
    start_date?: string
    end_date?: string
    employee_id?: string
  }
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function formatRate(rate: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(rate)
}

/* ── Column Definitions ─────────────────────────────────── */

const mileageColumns: HpiColumn<MileageRow>[] = [
  {
    header: "Date",
    width: "w-[120px] !flex-none",
    render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
  },
  {
    header: "Description",
    render: (row) => (
      <div className="text-sm">
        {row.case_name} ({row.miles} miles @ {formatRate(row.mileage_rate)})
      </div>
    ),
  },
  {
    header: "Amount",
    width: "w-[120px] !flex-none",
    align: "right",
    render: (row) => (
      <div className="text-sm font-medium">{formatCurrency(row.amount)}</div>
    ),
  },
]

const expenseColumns: HpiColumn<ExpenseRow>[] = [
  {
    header: "Date",
    width: "w-[120px] !flex-none",
    render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name}</div>,
  },
  {
    header: "Amount",
    width: "w-[120px] !flex-none",
    align: "right",
    render: (row) => (
      <div className="text-sm font-medium">{formatCurrency(row.amount)}</div>
    ),
  },
]

/* ── Component ──────────────────────────────────────────── */

export default function PaymentDetail({
  payment_date,
  payment_ref,
  mileage,
  cash_expenses,
  mileage_total,
  expense_total,
  payment_total,
  return_params,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Team", href: "/team" },
    { title: "Payment History", href: teamPaymentsPath(return_params) },
    { title: `Payment - ${formatDate(payment_date)}` },
  ]

  const hasBothSections = mileage.length > 0 && cash_expenses.length > 0
  const hasNoEntries = mileage.length === 0 && cash_expenses.length === 0

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Payment Detail - ${formatDate(payment_date)}`} />

      <div className="p-6">
        {/* Payment header */}
        <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-1">
          <div className="text-sm">
            <span className="font-semibold">Paid on:</span>{" "}
            {formatDate(payment_date)}
          </div>
          {payment_ref && (
            <div className="text-sm">
              <span className="font-semibold">Ref:</span> {payment_ref}
            </div>
          )}
        </div>

        {hasNoEntries && (
          <div className="py-8 text-center text-sm text-red-600">
            No expenses found for this entry
          </div>
        )}

        {/* Mileage section */}
        {mileage.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<MileageRow>
              title="Mileage"
              columns={mileageColumns}
              data={mileage}
              keyFn={(row) => `m-${row.id}`}
            />
            <div className="border-muted flex justify-end border-t px-4 py-2">
              <span className="mr-4 text-sm font-semibold">Total</span>
              <span className="w-[120px] text-right text-sm font-semibold">
                {formatCurrency(mileage_total)}
              </span>
            </div>
          </div>
        )}

        {/* Cash Expenses section */}
        {cash_expenses.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<ExpenseRow>
              title="Cash Expenses & Per Diem"
              columns={expenseColumns}
              data={cash_expenses}
              keyFn={(row) => `e-${row.id}`}
            />
            <div className="border-muted flex justify-end border-t px-4 py-2">
              <span className="mr-4 text-sm font-semibold">Total</span>
              <span className="w-[120px] text-right text-sm font-semibold">
                {formatCurrency(expense_total)}
              </span>
            </div>
          </div>
        )}

        {/* Payment total (only shown when both sections exist) */}
        {hasBothSections && (
          <HpiStatsBar
            className="mt-2"
            stats={[
              {
                label: "Payment Total",
                value: formatCurrency(payment_total),
              },
            ]}
          />
        )}

        {/* Return link */}
        <div className="mt-6 text-center">
          <Link
            href={teamPaymentsPath(return_params)}
            className="text-sm text-blue-600 hover:underline"
          >
            Return to Payment List
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
